import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import { extract_items, parse_response_data } from '@/utils/api_response_helpers';
import {
  CREATE_SLA_BINDING_URL,
  GET_FULL_CONFIG_BY_ENTITY_TYPE_URL,
  GET_SLA_BINDING_URL,
  LIST_SLA_CONFIGS_URL,
  REMOVE_SLA_BINDING_URL,
  get_request_config,
} from '@/utils/system_constants/constant.js';

export default {
  name: 'ServiceLayerBindings',
  mixins: [alert_mixin],
  props: {
    entityTypeId: {
      type: Number,
      required: false,
      default: null,
    },
    entityTypeName: {
      type: String,
      required: false,
      default: '',
    },
    entityConfigId: {
      type: Number,
      required: false,
      default: null,
    },
  },
  data: () => ({
    slas: [],
    sla_bindings: [],
    selected_sla_ids: [],
    creating_sla_binding: false,
    removing_binding_id: null,
    sla_dropdown_open: false,
    slas_loading: false,
    config_loading: false,
    resolved_entity_config_id: null,
  }),
  computed: {
    available_slas_count() {
      return Array.isArray(this.slas) ? this.slas.length : 0;
    },
    bound_slas_count() {
      return Array.isArray(this.sla_bindings) ? this.sla_bindings.length : 0;
    },
    selected_slas_count() {
      return Array.isArray(this.selected_sla_ids) ? this.selected_sla_ids.length : 0;
    },
    selected_slas_label() {
      if (this.slas_loading) return 'Loading SLAs...';
      if (!this.bindable_slas.length) return 'No SLA policies available';
      if (!this.selected_slas_count) return 'Select SLA(s) to bind';
      return `${this.selected_slas_count} SLA${this.selected_slas_count === 1 ? '' : 's'} selected`;
    },
    bindable_slas() {
      const bound_sla_ids = new Set(
        (Array.isArray(this.sla_bindings) ? this.sla_bindings : [])
          .map((binding) => Number(
            binding?.sla_config_id ??
            binding?.sla_config ??
            binding?.sla_id ??
            binding?.id
          ))
          .filter((id) => Number.isFinite(id) && id > 0)
      );

      return (Array.isArray(this.slas) ? this.slas : [])
        .filter((sla) => Number.isFinite(Number(sla?.id)))
        .filter((sla) => !bound_sla_ids.has(Number(sla.id)));
    },
  },
  mounted() {
    this.fetch_slas();
    this.fetch_sla_context();
    document.addEventListener('click', this.on_document_click, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.on_document_click, true);
  },
  methods: {
    on_document_click(event) {
      if (!this.sla_dropdown_open) return;
      const root = this.$refs?.slaMultiselect;
      if (root && !root.contains(event.target)) {
        this.sla_dropdown_open = false;
      }
    },
    toggle_sla_dropdown() {
      if (this.creating_sla_binding) return;
      this.sla_dropdown_open = !this.sla_dropdown_open;
    },
    format_date(date_value) {
      if (!date_value) return '-';
      const date = new Date(date_value);
      if (Number.isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
    },
    format_duration(duration_value) {
      const duration_parts = this.parse_duration_parts(duration_value);
      return duration_parts ? this.render_duration_parts(duration_parts) : '-';
    },
    parse_duration_parts(duration_value) {
      const raw_value = String(duration_value || '').trim();
      if (!raw_value || raw_value === '-') return null;

      const [day_part, time_part] = raw_value.includes(' ')
        ? raw_value.split(/\s+/, 2)
        : ['0', raw_value];

      const values = [day_part, ...String(time_part || '').split(':')].map((part) => Number(part));
      if (values.some((value) => Number.isNaN(value))) return null;

      const [days, hours, minutes, seconds] = values;
      const duration = {
        days,
        hours,
        minutes,
        seconds,
      };

      return duration;
    },
    render_duration_parts({ days, hours, minutes, seconds }) {
      const total_hours = (days * 24) + hours;
      const total_days = Math.floor(total_hours / 24);
      const remaining_hours = total_hours % 24;
      const parts = [
        [total_days, 'day'],
        [remaining_hours, 'hour'],
        [minutes, 'minute'],
      ]
        .filter(([value]) => value > 0)
        .map(([value, label]) => `${value} ${value === 1 ? label : `${label}s`}`);

      if (seconds > 0 && parts.length === 0) {
        parts.push(`${seconds} ${seconds === 1 ? 'second' : 'seconds'}`);
      }

      return parts.length ? parts.join(' ') : '0 minutes';
    },
    normalize_sla_item(item = {}) {
      const is_active = item.is_active !== false;

      const target_duration = item.target_duration || '-';
      const warning_threshold = item.warning_threshold || '-';
      const escalation_duration = item.escalation_duration || '-';

      const normalizedItem = {
        id: item.id,
        name: item.name || `SLA ${item.id}`,
        description: item.description || '',

        target_duration,
        warning_threshold,
        escalation_duration,

        target_duration_label: this.format_duration(item.target_duration),
        warning_threshold_label: this.format_duration(item.warning_threshold),
        escalation_duration_label: this.format_duration(item.escalation_duration),

        is_active,
        status_label: is_active ? 'Active' : 'Inactive',
        status_class: is_active ? 'sla-status-active' : 'sla-status-inactive',

        created_label: this.format_date(item.date_created || item.date_updated),
      };

      return normalizedItem;
    },
    normalize_sla_binding(item = {}, index = 0) {
      const matched_sla = (Array.isArray(this.slas) ? this.slas : [])
        .find((sla) => String(sla.id) === String(item?.sla_config_id ?? item?.sla_config ?? item?.id ?? ''));
      const mapped_item = {
        id: item?.id ?? item?.sla_binding_id ?? item?.binding_id ?? `${item?.sla_config_id || item?.sla_config || index}`,
        sla_config_id: Number(item?.sla_config_id ?? item?.sla_config ?? matched_sla?.id ?? 0) || null,
        name: item?.sla_name || matched_sla?.name || item?.name || `SLA ${index + 1}`,
        description: item?.description || matched_sla?.description || '',
        target_duration_label:
          item?.target_duration_label ||
          matched_sla?.target_duration_label ||
          this.format_duration(item?.target_duration || matched_sla?.target_duration),
        warning_threshold_label:
          item?.warning_threshold_label ||
          matched_sla?.warning_threshold_label ||
          this.format_duration(item?.warning_threshold || matched_sla?.warning_threshold),
        escalation_duration_label:
          item?.escalation_duration_label ||
          matched_sla?.escalation_duration_label ||
          this.format_duration(item?.escalation_duration || matched_sla?.escalation_duration),
        raw: item,
      };

      return mapped_item;
    },
    extract_sla_bindings(config_data = {}) {
      const candidates = [
        config_data?.sla_bindings,
        config_data?.entity_sla_bindings,
        config_data?.entity_config?.sla_bindings,
        config_data?.entity_config?.entity_sla_bindings,
      ];

      for (const candidate of candidates) {
        if (Array.isArray(candidate)) return candidate;
      }

      return [];
    },
    extract_sla_bindings_from_response(response_data = {}) {
      const candidates = [
        response_data?.data,
        response_data?.sla_bindings,
        response_data?.entity_sla_bindings,
        response_data?.items,
      ];

      for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
          return candidate;
        }
      }

      if (Array.isArray(response_data)) {
        return response_data;
      }

      return [];
    },
    parse_selected_ids() {
      return Array.isArray(this.selected_sla_ids)
        ? this.selected_sla_ids.map((id) => String(id)).filter(Boolean)
        : [];
    },
    get_entity_config_id() {
      const entity_type_id = Number(this.entityTypeId);

      return axios
        .get(
          GET_FULL_CONFIG_BY_ENTITY_TYPE_URL,
          get_request_config({
            params: {
              entity_type_id,
            },
          })
        )
        .then((response) => {
          const response_data = parse_response_data(response);
          const config_data = response_data.data || {};

          const entity_config_id =
            Number(config_data?.entity_config?.id || 0) || null;

          this.resolved_entity_config_id = entity_config_id;

          return {
            entity_config_id,
            config_data,
          };
        });
    },

    get_sla_bindings(entity_config_id) {
      return axios
        .get(
          GET_SLA_BINDING_URL,
          get_request_config({
            params: {
              entity_config_id,
            },
          })
        )
        .then((response) => {
          const response_data = parse_response_data(response);

          return this.extract_sla_bindings_from_response(response_data);
        });
    },

    fetch_sla_context() {
      let entity_config_id = Number(
        this.resolved_entity_config_id || this.entityConfigId || 0
      );

      if (Number.isFinite(entity_config_id) && entity_config_id > 0) {
        this.load_sla_bindings(entity_config_id);
        return;
      }

      this.get_entity_config_id()
        .then(({ entity_config_id }) => {
          if (!Number.isFinite(entity_config_id) || entity_config_id <= 0) {
            this.sla_bindings = [];
            return;
          }

          this.load_sla_bindings(entity_config_id);
        })
        .catch(() => {
          this.sla_bindings = [];
        });
    },

    load_sla_bindings(entity_config_id) {
      this.config_loading = true;

      this.get_sla_bindings(entity_config_id)
        .then((binding_items) => {
          this.sla_bindings = binding_items.map((binding, index) =>
            this.normalize_sla_binding(binding, index)
          );
        })
        .catch(() => {
          this.sla_bindings = [];
        })
        .finally(() => {
          this.config_loading = false;
        });
    },

    async fetch_slas() {
      this.slas_loading = true;
      try {
        const response = await axios.get(LIST_SLA_CONFIGS_URL, get_request_config());
        const response_data = parse_response_data(response);
        const items = extract_items(response)
          .concat(
            Array.isArray(response_data?.sla_configs) ? response_data.sla_configs : [],
            Array.isArray(response_data?.items) ? response_data.items : [],
          );
        this.slas = items.map((item) => this.normalize_sla_item(item));
      } catch {
        this.slas = [];
      } finally {
        this.slas_loading = false;
      }
    },
    async bind_single_sla(sla_id) {
      const payload = {
        entity_config_id: Number(this.resolved_entity_config_id || this.entityConfigId || 0),
        sla_config_id: sla_id,
      };

      const response = await axios.post(
        CREATE_SLA_BINDING_URL,
        payload,
        get_request_config()
      ).then((result) => parse_response_data(result)).catch(() => null);

      if (!response) return this.build_bind_failure_result(sla_id);
      if (response?.status && response.status !== 'success') {
        return this.build_bind_failure_result(sla_id, response?.message || 'Failed to bind SLA.');
      }

      const success_result = { ok: true, sla_id };
      return success_result;
    },
    build_bind_failure_result(sla_id, message = 'Failed to bind SLA.') {
      const failure_result = { ok: false, sla_id, message };
      return failure_result;
    },
    async bind_sla_batch(selected_ids) {
      const unique_ids = Array.from(new Set(selected_ids));
      const successes = [];
      const failures = [];

      for (const raw_id of unique_ids) {
        const sla_id = Number(raw_id);
        if (!Number.isFinite(sla_id) || sla_id <= 0) {
          failures.push({ sla_id: raw_id, message: 'Invalid SLA selected.' });
          continue;
        }

        const result = await this.bind_single_sla(sla_id);
        if (result.ok) successes.push(sla_id);
        else failures.push(result);
      }

      await this.fetch_sla_context();
      await this.fetch_slas();

      if (successes.length) {
        this.show_success(`${successes.length} SLA${successes.length === 1 ? '' : 's'} bound successfully.`);
      }

      if (failures.length) {
        this.show_error(
          failures.length === 1
            ? (failures[0]?.message || 'Failed to bind SLA.')
            : `${failures.length} SLA${failures.length === 1 ? '' : 's'} failed to bind.`
        );
      }

      this.selected_sla_ids = failures.map((failure) => String(failure.sla_id)).filter(Boolean);
      if (!failures.length) this.sla_dropdown_open = false;
    },
    async add_sla_bindings() {
      const selected_ids = this.parse_selected_ids();

      if (!selected_ids.length) {
        this.show_error('Please select at least one SLA to bind.');
        return;
      }

      if (this.creating_sla_binding) return;
      this.creating_sla_binding = true;

      try {
        await this.bind_sla_batch(selected_ids);
      } finally {
        this.creating_sla_binding = false;
      }
    },
    remove_sla_binding(binding) {
      const binding_id = Number(binding?.id);
      if (!Number.isFinite(binding_id) || binding_id <= 0) {
        this.show_error('Unable to remove this SLA binding.');
        return;
      }

      if (this.removing_binding_id === binding_id) return;

      this.show_confirm(
        'Remove SLA binding?',
        'This will unbind the SLA policy from this entity configuration.',
        async () => {
          this.removing_binding_id = binding_id;

          try {
            const response = await axios.post(
              REMOVE_SLA_BINDING_URL,
              { binding_id },
              get_request_config()
            );
            const response_data = parse_response_data(response);
            if (response_data?.status && response_data.status !== 'success') {
              this.show_error(response_data?.message || 'Failed to remove SLA binding.');
              return;
            }

            this.sla_bindings = this.sla_bindings.filter((existing) => Number(existing?.id) !== binding_id);
            await this.fetch_slas();
            this.show_success(response_data?.message || 'SLA binding removed.');
          } catch {
            this.show_error('Failed to remove SLA binding.');
          } finally {
            this.removing_binding_id = null;
          }
        }
      );
    },
    go_to_sla_overview() {
      this.$router.push({ name: 'ServiceLayerAgreement' });
    },
  },
};
